import {invoiceConstants} from '../constants';
import {request} from '../services/request';
import {backendEndpoints} from '../../api';
import {apiHelpers} from '../../utils/helpers';

const errorHandlerProps = {disableAlert: true};

// Shared helper to reduce duplication in action creators.
// method: 'get' | 'post' | 'put' | 'delete' | 'patch'
// url: backendEndpoints key or a pattern used with apiHelpers.getUrl when urlParams provided
// urlParams: optional object for apiHelpers.getUrl
// useErrorHandler: whether to attach errorHandlerProps
const doRequest = ({ method = 'get', url, urlParams, query, data, successCallback, errorCallback, useErrorHandler = true }) => {
  return (dispatch) => {
    const opts = {
      dispatch,
      url: urlParams ? apiHelpers.getUrl(url, urlParams) : url,
      successCallback,
      errorCallback,
      errorHandlerProps
    };

    if (query !== undefined) opts.query = query;
    if (data !== undefined) opts.data = data;
    if (useErrorHandler) opts.errorHandlerProps = errorHandlerProps;

    // call appropriate request method
    if (method === 'get') return request.get(opts);
    if (method === 'post') return request.post(opts);
    if (method === 'put') return request.put(opts);
    if (method === 'delete') return request.delete(opts);
    if (method === 'patch') return request.patch(opts);

    // fallback
    return request[method] ? request[method](opts) : request.get(opts);
  };
}

const getInvoices = ({query = null, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'get', url: backendEndpoints.GET_INVOICES, query, successCallback, errorCallback });
}

const getInvoice = (id, successCallback, errorCallback) => {
  return doRequest({ method: 'get', url: backendEndpoints.GET_INVOICE, urlParams: { id }, successCallback, errorCallback });
}

const getInvoiceApproval = ({id, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'get', url: backendEndpoints.GET_INVOICE_APPROVAL, urlParams: { id }, successCallback, errorCallback });
}

const getAccountingInvoice = (id, successCallback, errorCallback) => {
  return doRequest({ method: 'get', url: backendEndpoints.GET_ACCOUNTING_INVOICE, urlParams: { id }, successCallback, errorCallback });
}

const getBatchInvoices = ({data, successCallback, errorCallback}) => {
  return doRequest({ method: 'post', url: backendEndpoints.POST_INVOICES_BATCH, data, successCallback, errorCallback });
}

const batchCreateInvoices = (data, successCallback, errorCallback) => {
  return doRequest({ method: 'post', url: backendEndpoints.POST_INVOICES_BATCH_CREATE, data, successCallback, errorCallback });
}

const batchDeleteInvoices = (invoices, successCallback, errorCallback) => {
  return doRequest({ method: 'delete', url: backendEndpoints.DELETE_INVOICES_BATCH, data: { invoices }, successCallback, errorCallback });
}

const getInvoicesTotalUnmatched = (successCallback, errorCallback) => {
  return doRequest({ method: 'get', url: backendEndpoints.GET_INVOICES_STATS_UNMATCHED, successCallback, errorCallback });
}

const updateInvoice = (id, data, successCallback, errorCallback) => {
  return doRequest({ method: 'put', url: backendEndpoints.PUT_INVOICE_UPDATE, urlParams: { id }, data, successCallback, errorCallback });
}

const linkExpense = ({id, data, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'put', url: backendEndpoints.PUT_INVOICE_LINK_EXPENSE, urlParams: { id }, data, successCallback, errorCallback });
}

const unlinkExpense = ({data, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'put', url: backendEndpoints.PUT_INVOICE_UNLINK_EXPENSE, data, successCallback, errorCallback });
}

const downloadAccountingInvoice = ({id, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'get', url: backendEndpoints.GET_ACCOUNTING_INVOICE_ATTACHMENT, urlParams: { id }, successCallback, errorCallback });
}

const downloadInvoice = (id, successCallback, errorCallback) => {
  return doRequest({ method: 'get', url: backendEndpoints.GET_INVOICE_ATTACHMENT, urlParams: { id }, successCallback, errorCallback });
}

const downloadZipInvoices = ({query, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'get', url: backendEndpoints.GET_DOWNLOAD_ZIP_INVOICES, query, successCallback, errorCallback });
}

const downloadCsvInvoices = ({query, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'get', url: backendEndpoints.GET_INVOICES_EXPORT, query, successCallback, errorCallback });
}

const getInvoiceNeighbours = ({id, query, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'get', url: backendEndpoints.GET_INVOICE_NEIGHBOURS, urlParams: { id }, query, successCallback, errorCallback });
}

const getInvoiceRelatedExpenses = ({id, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'get', url: backendEndpoints.GET_INVOICE_RELATED_EXPENSES, urlParams: { id }, successCallback, errorCallback });
}

const payInvoiceOutside = ({id, paid = true, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'put', url: backendEndpoints.PUT_INVOICE_PAID_OUTSIDE, urlParams: { id }, data: { paid_outside: paid }, successCallback, errorCallback });
}


const assignBudgetToInvoice = ({id, data, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'put', url: backendEndpoints.PUT_INVOICE_ASSIGN_BUDGET_REQUEST, urlParams: { id }, data, successCallback, errorCallback });
}

const requestInvoicePayment = ({id, data, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'post', url: backendEndpoints.POST_INVOICE_REQUEST_PAYMENT, urlParams: { id }, data, successCallback, errorCallback });
}

const cancelPaymentRequest = ({id, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'post', url: backendEndpoints.PUT_INVOICE_CANCEL_PAYMENT_REQUEST, urlParams: { id }, successCallback, errorCallback });
}

const approveInvoiceApproval = ({id, approvalId, data, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'put', url: backendEndpoints.PUT_INVOICE_APPROVE_APPROVAL, urlParams: { id, approvalId }, data, successCallback, errorCallback });
}

const rejectInvoiceApproval = ({id, approvalId, data, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'put', url: backendEndpoints.PUT_INVOICE_REJECT_APPROVAL, urlParams: { id, approvalId }, data, successCallback, errorCallback });
}

const reopenInvoiceApproval = ({id, approvalId, data, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'patch', url: backendEndpoints.PATCH_INVOICE_REOPEN_APPROVAL, urlParams: { id, approvalId }, data, successCallback, errorCallback });
}

const reassignInvoice = ({id, data, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'put', url: backendEndpoints.PUT_INVOICE_REASSIGN, urlParams: { id }, data, successCallback, errorCallback });
}

const saveInvoicesListUrl = (url, query) => {
  return (dispatch) => {
    dispatch({ type: invoiceConstants.SAVE_INVOICES_LIST_URL, payload: { url, query } })
  }
}

const getRecommendedBudgetRequests = ({id, successCallback, errorCallback} = {}) => {
  return doRequest({ method: 'get', url: backendEndpoints.GET_INVOICE_RECOMMENDED_BUDGET_REQUESTS, urlParams: { id }, successCallback, errorCallback });
}

export const invoicesActions = {
  getInvoice,
  getInvoices,
  getInvoiceApproval,
  getInvoiceNeighbours,
  getInvoiceRelatedExpenses,
  getInvoicesTotalUnmatched,
  getAccountingInvoice,
  getBatchInvoices,
  assignBudgetToInvoice,
  batchCreateInvoices,
  batchDeleteInvoices,
  cancelPaymentRequest,
  downloadAccountingInvoice,
  downloadInvoice,
  downloadZipInvoices,
  downloadCsvInvoices,
  linkExpense,
  payInvoiceOutside,
  requestInvoicePayment,
  saveInvoicesListUrl,
  unlinkExpense,
  updateInvoice,

  approveInvoiceApproval,
  rejectInvoiceApproval,
  reassignInvoice,
  reopenInvoiceApproval,
  getRecommendedBudgetRequests
};
